// ── BCH Rooms — WebRTC signalling over Nostr ──────────────────────
//
// WebRTC needs a low-latency, bidirectional side-channel to exchange
// SDP offers/answers and ICE candidates. Here that channel is Nostr:
// ephemeral kind 25050 events, encrypted to the recipient.
//
// ── Why this does not use useNostrQuery ──────────────────────────
//
// useNostrQuery is built for feeds: it caches results for 5 minutes,
// batches incoming events before flushing to React state, and replays
// cached events on mount. Every one of those is wrong for signalling —
// a cached offer is worthless, and a batched ICE candidate arrives too
// late to be useful. So this hook opens its own raw subscription and
// dispatches each event the moment it lands.
//
// ── Why payloads are encrypted ───────────────────────────────────
//
// ICE candidates contain IP addresses, including the participant's local
// network addresses. Publishing those in plaintext to public relays
// would deanonymise everyone in the room to anyone watching. NIP-04
// encryption to the recipient's pubkey keeps them private; it is
// supported by every signer this app accepts (nsec, NIP-07, NIP-46).

import { useEffect, useRef, useCallback } from 'react';
import { getEventHash, type Event, type UnsignedEvent } from 'nostr-tools';
import { getReadRelays, getRelays } from './useNostrPublish';
import { ROOM_SIGNAL_KIND } from '../lib/rooms';
import type { SignalPayload } from '../lib/roomAudio';

interface NostrSigner {
  getPublicKey(): Promise<string>;
  signEvent(event: UnsignedEvent & { id?: string }): Promise<Event>;
  nip04?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

function getSigner(): NostrSigner | null {
  const signer = (window as unknown as { nostr?: NostrSigner }).nostr;
  return signer ?? null;
}

export interface UseRoomSignalOptions {
  /** Room `a` coordinate. Signalling is scoped to it so rooms never cross. */
  coordinate: string;
  /** Our own pubkey; used to ignore our own echoes. */
  selfPubkey: string | undefined;
  /** Only subscribe while actually in the room. */
  enabled: boolean;
  /** Called for every inbound payload, in arrival order. */
  onSignal: (payload: SignalPayload) => void;
}

export interface UseRoomSignalResult {
  /**
   * Send a payload to one peer, or broadcast when `to` is empty.
   *
   * Broadcasts (`join` / `leave`) are unencrypted because they have no
   * single recipient and carry nothing sensitive — just "I am here" and
   * a role. Everything with an SDP or ICE candidate in it is encrypted.
   */
  sendSignal: (to: string, payload: SignalPayload) => Promise<void>;
}

/** Payload types that must never be published in the clear. */
const SENSITIVE = new Set(['offer', 'answer', 'ice']);

export function useRoomSignal(
  { coordinate, selfPubkey, enabled, onSignal }: UseRoomSignalOptions,
): UseRoomSignalResult {
  // Keep the callback in a ref so re-renders never tear down the
  // subscription — resubscribing mid-handshake loses candidates.
  const onSignalRef = useRef(onSignal);
  useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);

  const socketsRef = useRef<WebSocket[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  // ── Subscribe ───────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !coordinate || !selfPubkey) return;

    const signer = getSigner();
    const subId = `room-sig-${Math.random().toString(36).slice(2, 10)}`;
    // Subscribe to the whole room rather than just `#p` = us: broadcasts
    // (`join` / `leave`) carry no `p` tag at all, so a `#p`-only filter
    // would silently never deliver them and the mesh would never form.
    // Events addressed to someone else are dropped client-side below.
    const filter = {
      kinds: [ROOM_SIGNAL_KIND],
      '#a': [coordinate],
      since: Math.floor(Date.now() / 1000) - 30,
    };

    const sockets: WebSocket[] = [];
    let closed = false;

    const handle = async (ev: Event) => {
      if (ev.pubkey === selfPubkey) return;      // our own echo

      // Directed events carry a `p` tag; ignore those meant for someone
      // else. Untagged events are room broadcasts and are for everyone.
      const recipients = ev.tags.filter(t => t[0] === 'p').map(t => t[1]);
      if (recipients.length > 0 && !recipients.includes(selfPubkey)) return;

      if (seenRef.current.has(ev.id)) return;    // duplicate across relays
      seenRef.current.add(ev.id);
      // Bound the dedupe set; a long room would otherwise grow it forever.
      if (seenRef.current.size > 2000) seenRef.current.clear();

      let raw = ev.content;
      // Encrypted payloads look like NIP-04 ciphertext ("<base64>?iv=<base64>").
      if (raw.includes('?iv=')) {
        if (!signer?.nip04) return;
        try {
          raw = await signer.nip04.decrypt(ev.pubkey, raw);
        } catch {
          return; // not for us, or signer refused
        }
      }

      let payload: SignalPayload;
      try {
        payload = JSON.parse(raw) as SignalPayload;
      } catch {
        return;
      }
      if (!payload || typeof payload.type !== 'string') return;
      // Trust the *event* author over the payload's self-reported `from`,
      // so a peer cannot impersonate someone else by lying in the body.
      payload.from = ev.pubkey;
      onSignalRef.current(payload);
    };

    for (const url of getReadRelays().slice(0, 4)) {
      try {
        const ws = new WebSocket(url);
        sockets.push(ws);
        ws.onopen = () => {
          if (closed) { try { ws.close(); } catch { /* noop */ } return; }
          try { ws.send(JSON.stringify(['REQ', subId, filter])); } catch { /* noop */ }
        };
        ws.onmessage = e => {
          try {
            const data = JSON.parse(e.data as string);
            if (!Array.isArray(data)) return;
            if (data[0] === 'EVENT' && data[1] === subId && data[2]) {
              const ev = data[2] as Event;
              if (!ev.id || !Array.isArray(ev.tags)) return;
              void handle(ev);
            }
          } catch { /* malformed frame */ }
        };
        ws.onerror = () => { /* other relays may still work */ };
      } catch { /* skip unusable relay */ }
    }
    socketsRef.current = sockets;

    return () => {
      closed = true;
      for (const ws of sockets) {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(['CLOSE', subId]));
          }
          ws.close();
        } catch { /* already gone */ }
      }
      socketsRef.current = [];
    };
  }, [enabled, coordinate, selfPubkey]);

  // ── Send ────────────────────────────────────────────────────────
  const sendSignal = useCallback(async (to: string, payload: SignalPayload) => {
    const signer = getSigner();
    if (!signer || !selfPubkey || !coordinate) return;

    let content = JSON.stringify(payload);
    const tags: string[][] = [['a', coordinate]];

    if (to) {
      tags.push(['p', to]);
      if (SENSITIVE.has(payload.type)) {
        if (!signer.nip04) return; // cannot send SDP/ICE in the clear
        try {
          content = await signer.nip04.encrypt(to, content);
        } catch {
          return;
        }
      }
    }

    const unsigned: UnsignedEvent = {
      kind: ROOM_SIGNAL_KIND,
      content,
      tags,
      created_at: Math.floor(Date.now() / 1000),
      pubkey: selfPubkey,
    };

    let signed: Event;
    try {
      signed = await signer.signEvent({ ...unsigned, id: getEventHash(unsigned) });
    } catch {
      return;
    }

    // Fire-and-forget on short-lived sockets. Signalling is latency-
    // sensitive and self-healing (a lost candidate is retried by ICE), so
    // publishing is deliberately not awaited past the send.
    for (const url of getRelays().slice(0, 4)) {
      try {
        const ws = new WebSocket(url);
        ws.onopen = () => {
          try { ws.send(JSON.stringify(['EVENT', signed])); } catch { /* noop */ }
          setTimeout(() => { try { ws.close(); } catch { /* noop */ } }, 1500);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch { /* skip */ }
    }
  }, [selfPubkey, coordinate]);

  return { sendSignal };
}
